<!-- app/frontend/pages/Web/Users/Profile.svelte -->
<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { usePage, useForm } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button'
  import { Input } from '$lib/components/ui/input'
  import { Label } from '$lib/components/ui/label'
  import * as Select from '$lib/components/ui/select'
  import * as Card from '$lib/components/ui/card'
  import { Separator } from '$lib/components/ui/separator'
  import { Avatar, AvatarFallback, AvatarImage } from "$lib/components/ui/avatar"

  // 1. 接收 props
  let { errors = {} } = $props()

  // 2. 获取 usePage 实例
  const page = usePage()

  // 3. 提取全局共享的数据
  const currentLocale = $derived(page.props.current_locale || 'en')
  const availableLocales = $derived(page.props.available_locales || [])
  const user = $derived(page.props.auth?.user || {})
  
  // 核心：直接从 user 对象中提取 metadata
  const userMetadata = $derived(user.metadata || {})

  const t = $derived(page.props.translations?.profile || {})

  // 4. 表单初始化：在 Svelte 5 中 form 是响应式对象而非 Store
  const form = useForm({
    name: user.name || '',
    mobile: user.mobile || '',
    timezone: userMetadata.timezone || 'Asia/Shanghai',
    locale: userMetadata.locale || currentLocale,
    current_password: '',
    password: '',
    password_confirmation: ''
  })

  // 辅助函数：安全解析 Rails / 自定义错误格式
  function getErrorMessage(fieldName) {
    const err = form.errors[fieldName] || errors[fieldName]
    if (!err) return null
    return Array.isArray(err) ? err.join(', ') : err
  }

  function handleSubmit(e) {
    e.preventDefault()
    form.patch('/profile', {
      preserveScroll: true,
      onSuccess: () => {
        form.current_password = ''
        form.password = ''
        form.password_confirmation = ''
      }
    })
  }
</script>

<div class="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
  <Card.Root>
    <Card.Header class="flex flex-row items-center justify-between space-y-0 pb-6">
      <div>
        <Card.Title class="text-2xl">{t.title || '个人资料设置'}</Card.Title>
        <Card.Description class="mt-1">{t.description || '管理您的个人信息及账号安全设定'}</Card.Description>
      </div>
      
      <!-- Gravatar / 用户头像 -->
      <div class="flex items-center gap-3">
        <Avatar class="h-8 w-8 border shadow-sm">
          <AvatarImage src={user.avatar_url} alt={user.name} />
          <AvatarFallback class="bg-primary/10 text-primary text-xs uppercase">
            {(user.name || user.email_address || 'U').substring(0, 2)}
          </AvatarFallback>
        </Avatar>
      </div>
    </Card.Header>

    <Card.Content>
      <form onsubmit={handleSubmit} class="space-y-6">
        <!-- 基本信息区域 -->
        <div class="space-y-4">
          <h3 class="text-lg font-medium">{t.basic_info || '基本信息'}</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- 邮箱（禁用编辑） -->
            <div class="space-y-2">
              <Label for="email">{t.email || '电子邮箱'}</Label>
              <Input
                id="email"
                type="email"
                value={user.email_address}
                disabled
                class="bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p class="text-xs text-muted-foreground">{t.email_disabled_tip || '邮箱作为账号标识不可修改'}</p>
            </div>

            <!-- 姓名 / 昵称 -->
            <div class="space-y-2">
              <Label for="name">{t.name || '姓名'}</Label>
              <Input
                id="name"
                type="text"
                bind:value={form.name}
                placeholder="请输入姓名"
              />
              {#if getErrorMessage('name')}
                <p class="text-xs text-destructive">{getErrorMessage('name')}</p>
              {/if}
            </div>

            <!-- 手机号码 -->
            <div class="space-y-2">
              <Label for="mobile">{t.mobile || '手机号码'}</Label>
              <Input
                id="mobile"
                type="text"
                bind:value={form.mobile}
                placeholder="请输入手机号码"
              />
              {#if getErrorMessage('mobile')}
                <p class="text-xs text-destructive">{getErrorMessage('mobile')}</p>
              {/if}
            </div>

            <!-- 时区选择 (shadcn Select) -->
            <div class="space-y-2">
              <Label for="timezone">{t.timezone || '时区设置'}</Label>
              <Select.Root type="single" bind:value={form.timezone}>
                <Select.Trigger class="w-full">
                  {form.timezone || '请选择时区'}
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="Asia/Shanghai">Asia/Shanghai (GMT+8)</Select.Item>
                  <Select.Item value="UTC">UTC</Select.Item>
                  <Select.Item value="America/New_York">America/New_York (EST)</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>

            <!-- 界面语言选择 (shadcn Select) -->
            <div class="space-y-2">
              <Label for="locale">{t.locale || '界面语言'}</Label>
              <Select.Root type="single" bind:value={form.locale}>
                <Select.Trigger class="w-full">
                  {form.locale === 'zh' ? '简体中文' : 'English'}
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="zh">简体中文</Select.Item>
                  <Select.Item value="en">English</Select.Item>
                </Select.Content>
              </Select.Root>
            </div>
          </div>
        </div>

        <Separator class="my-6" />

        <!-- 修改密码区域 -->
        <div class="space-y-4">
          <div>
            <h3 class="text-lg font-medium">{t.change_password_title || '修改密码'}</h3>
            <p class="text-xs text-muted-foreground mt-1">{t.change_password_tip || '如果不打算更新密码，请留空'}</p>
          </div>

          <div class="space-y-4">
            <!-- 当前密码 -->
            <div class="max-w-md space-y-2">
              <Label for="current_password">{t.current_password || '当前密码'}</Label>
              <Input
                id="current_password"
                type="password"
                bind:value={form.current_password}
                placeholder="••••••••"
              />
              {#if getErrorMessage('current_password')}
                <p class="text-xs text-destructive">
                  {getErrorMessage('current_password')}
                </p>
              {/if}
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- 新密码 -->
              <div class="space-y-2">
                <Label for="password">{t.new_password || '新密码'}</Label>
                <Input
                  id="password"
                  type="password"
                  bind:value={form.password}
                  placeholder="••••••••"
                />
                {#if getErrorMessage('password')}
                  <p class="text-xs text-destructive">
                    {getErrorMessage('password')}
                  </p>
                {/if}
              </div>

              <!-- 确认新密码 -->
              <div class="space-y-2">
                <Label for="password_confirmation">{t.confirm_password || '确认新密码'}</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  bind:value={form.password_confirmation}
                  placeholder="••••••••"
                />
                {#if getErrorMessage('password_confirmation')}
                  <p class="text-xs text-destructive">
                    {getErrorMessage('password_confirmation')}
                  </p>
                {/if}
              </div>
            </div>
          </div>
        </div>

        <!-- 提交按钮 -->
        <div class="flex justify-end pt-4">
          <Button type="submit" disabled={form.processing}>
            {form.processing ? (t.saving || '保存中...') : (t.save_changes || '保存更改')}
          </Button>
        </div>
      </form>
    </Card.Content>
  </Card.Root>
</div>